import {inject, Injectable} from '@angular/core';
import {RecordsDataRequestQueryParams} from "../../../../types/records-table.types";
import {InitialFiltersData, MinMaxDate, RangeData} from "../types/line-chart-filters.types";
import {UtilityService} from "../../../../services/utility.service";
import {fieldsLineChart} from "../configs/line-chart-modal-config";
import {FieldsLineChartParams, LineChartDialogData} from "../types/line-chart-modal.types";
import {LineChartModalService} from "./line-chart-modal.service";

@Injectable({
  providedIn: 'root'
})
export class LineChartFiltersService {
  private lineChartModalService: LineChartModalService = inject(LineChartModalService);
  private utilityService: UtilityService = inject(UtilityService);

  public getInitialFiltersData(lineChartDialogData: LineChartDialogData): InitialFiltersData {
    const selectProductsData = lineChartDialogData.fields.find(field => field.key === 'productId')?.metaSelectKeyList || [];
    const columnsSelectData = this.lineChartModalService.getColumnsSelectData(lineChartDialogData.fields);
    const selectArticleData = lineChartDialogData.fields.find(field => field.key === 'article')?.metaSelectKeyList || [];

    return {
      selectProductsData,
      columnsSelectData,
      selectArticleData,
      filters: {
        selectedColumns: this.getAvailableChartFields(columnsSelectData),
        selectedYear: this.getSelectedYear(lineChartDialogData.queryParams) || 0,
        selectedProductId: this.getSelectedProductId(lineChartDialogData.queryParams) || '',
        selectedArticleId: this.getSelectedArticleId(lineChartDialogData.queryParams) || ''
      }
    };
  }

  public getMinMaxDates(queryParams?: RecordsDataRequestQueryParams): MinMaxDate | null {
    if (!queryParams) { return null; }

    const dates = Object.entries(queryParams)
      .filter(([key, value]) => key.startsWith('date') && typeof value === 'string' && value)
      .map(([, value]) => this.utilityService.convertToDate(value as string))
      .filter((date): date is Date => date !== null);

    if (!dates.length) { return null; }

    const minTimestamp = Math.min(...dates.map(d => d.getTime()));
    const maxTimestamp = Math.max(...dates.map(d => d.getTime()));

    if (!isFinite(minTimestamp) || !isFinite(maxTimestamp)) { return null; }

    return {
      dateFrom: new Date(minTimestamp),
      dateTo: new Date(maxTimestamp)
    };
  }

  public formatDateRangeForDisplay(dateRange: MinMaxDate): RangeData {
    return {
      dateFrom: this.utilityService.formatDateToRussianStyle(dateRange.dateFrom),
      dateTo: this.utilityService.formatDateToRussianStyle(dateRange.dateTo)
    };
  }

  private getAvailableChartFields(columnsSelectData: Array<FieldsLineChartParams>): Array<string> {
    return fieldsLineChart
      .map(field => field.key)
      .filter(key => columnsSelectData.some(column => column.key === key));
  }

  private getSelectedProductId(queryParams?: RecordsDataRequestQueryParams): string | null {
    if (!queryParams) { return null; }

    return queryParams['productId'] || null;
  }

  private getSelectedArticleId(queryParams?: RecordsDataRequestQueryParams): string | null {
    if (!queryParams) { return null; }

    return queryParams['article']?.at(0) || null;
  }

  private getSelectedYear(queryParams?: RecordsDataRequestQueryParams): number | null {
    if (!queryParams) { return null; }

    return queryParams['year'] || null;
  }
}
